import React, { useEffect, useState } from "react";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel, PanelHeader } from "../../components/panel/panel.jsx";
import Button from "../../components/Button.tsx";
import Loader from "../../components/Loader.tsx";
import { useNavigate, useParams } from "react-router-dom";
import { notification } from "../../utils/Notifications.tsx";
import {
  getLoanById,
  updateLoan,
  getLoanByPartner,
} from "../../services/loans.service.ts";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  getAllActivePartners,
  fetchPartner,
} from "../../services/partner.service.ts";
import S3Service from "../../services/s3.service.ts";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import dayjs from "dayjs";
import { DatePicker, ConfigProvider } from "antd";
import esES from "antd/es/locale/es_ES";
import "dayjs/locale/es";
import { getCurrencyFormat, parseCurrency } from "../../utils/index.ts";
dayjs.locale("es");

const getMonthEnd = (d: any) => dayjs(d).endOf("month").date();
const normalizeStartToFortnight = (d: any) => {
  const date = dayjs(d);
  const day = date.date();
  return day <= 15 ? date.startOf("month") : date.date(16);
};
const normalizeEndToFortnight = (d: any) => {
  const date = dayjs(d);
  const day = date.date();
  return day <= 15 ? date.date(15) : date.date(getMonthEnd(date));
};
const countFortnights = (start: any, end: any) => {
  let s = normalizeStartToFortnight(start);
  const e = normalizeEndToFortnight(end);
  if (s.isAfter(e, "day")) return 0;
  let count = 0;
  while (!s.isAfter(e, "day")) {
    const currentEnd = s.date() <= 15 ? s.date(15) : s.date(getMonthEnd(s));
    count += 1;
    s = currentEnd.add(1, "day");
    s = normalizeStartToFortnight(s);
  }
  return count;
};

const EditLoan = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [profileUrl, setProfileUrl] = useState("");
  const [loan, setLoan] = useState(null);
  const [requestAmountInput, setRequestAmountInput] = useState("");
  const [isEditingRequestAmount, setIsEditingRequestAmount] = useState(false);
  const [adminAmountInput, setAdminAmountInput] = useState("");
  const [isEditingAdminAmount, setIsEditingAdminAmount] = useState(false);
  const [fortnightsCount, setFortnightsCount] = useState(0);
  const [activeTab, setActiveTab] = useState("info");
  const [partnerHistory, setPartnerHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<any>(null);
  useAppSettings();

  const validationSchema = Yup.object().shape({
    partnerId: Yup.number().required("Colaborador requerido"),
    adminAmount: Yup.number().when(["approved", "isProposal"], {
      is: (approved, isProposal) =>
        (approved === true || isProposal === true) && !isAlreadyProcessed,
      then: (schema) =>
        schema
          .positive("Es necesario el monto aprobado")
          .required("Es necesario el monto aprobado"),
      otherwise: (schema) => schema.optional(),
    }),

    startDayPay: Yup.number().when("approved", {
      is: true,
      then: (schema) => (!isAlreadyProcessed ? schema.required("Periodo requerido") : schema.optional()),
      otherwise: (schema) => schema.optional(),
    }),
    endDayPay: Yup.number().when("approved", {
      is: true,
      then: (schema) => (!isAlreadyProcessed ? schema.required("Periodo requerido") : schema.optional()),
      otherwise: (schema) => schema.optional(),
    }),
    approved: Yup.boolean().when([], {
      is: () => !isAlreadyProcessed,
      then: (schema) => schema.required("Debe seleccionar una decisión"),
      otherwise: (schema) => schema.optional(),
    }),
    reviewerComment: Yup.string().when("isRejected", {
      is: true,
      then: (schema) =>
        !isAlreadyProcessed
          ? schema.required("El comentario es obligatorio al rechazar")
          : schema.optional(),
      otherwise: (schema) => schema.optional(),
    }),
    proposalComment: Yup.string().when("isProposal", {
      is: true,
      then: (schema) =>
        !isAlreadyProcessed
          ? schema.required("El comentario es obligatorio para la propuesta")
          : schema.optional(),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const formik = useFormik({
    validationSchema,
    initialValues: {
      partnerId: "",
      requestAmount: "",
      adminAmount: "",
      amountDiscount: "",
      startDayPay: null,
      endDayPay: null,
      note: "",
      approved: false,
      isRejected: false,
      isProposal: false,
      reviewerComment: "",
      fortnights: null,
      amountPerFortnight: "",
      requestedReceiveDate: null,
      proposalAccepted: null,
      proposalComment: "",
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload: any = {
          partnerId: Number(values.partnerId),
          adminAmount: Number(values.adminAmount),
          amountDiscount: Number(values.amountDiscount),
          note: values.note || undefined,
          approved: values.approved,
          isRejected: values.isRejected,
          isProposal: values.isProposal,
          reviewerComment: values.reviewerComment || undefined,
          proposalAccepted: values.proposalAccepted,
          proposalComment: values.proposalComment || undefined,
        };
        if (typeof values.startDayPay === "number")
          payload.startDayPay = values.startDayPay;
        if (typeof values.endDayPay === "number")
          payload.endDayPay = values.endDayPay;
        if (typeof values.fortnights === "number")
          payload.fortnights = values.fortnights;
        if (typeof values.amountPerFortnight === "number")
          payload.amountPerFortnight = values.amountPerFortnight;
        if (typeof values.requestedReceiveDate === "number")
          payload.requestedReceiveDate = values.requestedReceiveDate;

        await updateLoan(Number(id), payload);
        notification("Éxito", "Préstamo actualizado correctamente", "success");
        navigate("/prestamos");
      } catch (e: any) {
        notification("Error", e?.message || "No se pudo actualizar", "danger");
      } finally {
        setLoading(false);
      }
    },
  });

  const loadPartnerInfo = async (partnerId: number) => {
    try {
      setLoading(true);
      const partner = await fetchPartner(partnerId);
      setSelectedPartner(partner);
      const curp = partner?.person?.curp;
      if (curp) {
        try {
          const file = await S3Service.getFile("PERFIL_" + curp);
          setProfileUrl(file?.url || "");
        } catch {
          setProfileUrl("");
        }
      } else {
        setProfileUrl("");
      }
    } catch (error) {
      setSelectedPartner(null);
      setProfileUrl("");
    } finally {
      setLoading(false);
    }
  };

  const loadPartnerHistory = async (partnerId: number) => {
    try {
      setLoadingHistory(true);
      const history = await getLoanByPartner(partnerId);
      setPartnerHistory(history || []);
    } catch (error) {
      console.error("Error loading partner history:", error);
      setPartnerHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setInitialLoading(true);
        const [loanData] = await Promise.all([getLoanById(Number(id))]);

        setLoan(loanData);

        const formValues = {
          partnerId: loanData.partner?.id?.toString() || "",
          requestAmount: (loanData.requestAmount ?? "").toString(),
          adminAmount: (loanData.adminAmount ?? "").toString(),
          amountDiscount: (loanData.amountDiscount ?? "").toString(),
          startDayPay: loanData.startDayPay,
          endDayPay: loanData.endDayPay,
          note: loanData.note || "",
          approved: loanData.approved || false,
          isRejected: loanData.isRejected || false,
          isProposal: loanData.isProposal || false,
          reviewerComment: loanData.reviewerComment || "",
          fortnights: loanData.fortnights || null,
          amountPerFortnight: (loanData.amountPerFortnight ?? "").toString(),
          requestedReceiveDate: loanData.requestedReceiveDate || null,
          proposalAccepted: loanData.proposalAccepted || null,
          proposalComment: loanData.proposalComment || "",
        } as any;
        formik.setValues(formValues);

        if (loanData.startDayPay && loanData.endDayPay) {
          const ns = normalizeStartToFortnight(
            dayjs.unix(loanData.startDayPay)
          );
          const ne = normalizeEndToFortnight(dayjs.unix(loanData.endDayPay));
          const count = countFortnights(ns, ne);
          setFortnightsCount(count);
        } else {
          setFortnightsCount(0);
        }

        if (loanData.partner?.id) {
          await loadPartnerInfo(loanData.partner.id);
          await loadPartnerHistory(loanData.partner.id);
        }

        const partnersData = await getAllActivePartners();
        setPartners(partnersData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        notification("Error", "No se pudo cargar los datos", "danger");
        navigate("/prestamos");
      } finally {
        setInitialLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  useEffect(() => {
    if (!isEditingRequestAmount) {
      const val = formik.values.requestAmount as any;
      if (val === null || val === undefined || val === "") {
        setRequestAmountInput("");
      } else {
        setRequestAmountInput(getCurrencyFormat(Number(val)));
      }
    }
  }, [formik.values.requestAmount, isEditingRequestAmount]);

  useEffect(() => {
    if (!isEditingAdminAmount) {
      const val = formik.values.adminAmount as any;
      if (val === null || val === undefined || val === "") {
        setAdminAmountInput("");
      } else {
        setAdminAmountInput(getCurrencyFormat(Number(val)));
      }
    }
  }, [formik.values.adminAmount, isEditingAdminAmount]);

  useEffect(() => {
    const currentAmount = Number(formik.values.adminAmount);
    if (currentAmount > 0 && fortnightsCount > 0) {
      const perQuincena = currentAmount / fortnightsCount;
      formik.setFieldValue("amountDiscount", perQuincena.toFixed(2));
    }
  }, [formik.values.adminAmount, fortnightsCount]);

  const displayCurrency = (value: any) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    if (!isFinite(num)) return "";
    return getCurrencyFormat(num);
  };

  const errorFor = (name: string) =>
    formik.touched[name as any] && formik.errors[name as any] ? (
      <div className="invalid-feedback" style={{ display: "block" }}>
        {formik.errors[name as any] as any}
      </div>
    ) : null;

  const getLoanStatus = () => {
    if (!loan) return "";
    if (loan.approved) return "APROBADO";
    if (loan.isRejected) return "RECHAZADO";
    return "PENDIENTE";
  };

  const getStatusColor = () => {
    if (!loan) return "bg-secondary";
    if (loan.approved) return "bg-success";
    if (loan.isRejected) return "bg-danger";
    return "bg-warning";
  };

  const isAlreadyProcessed = loan && (loan.approved || loan.isRejected);

  const handlePeriodDateChange = (range: any) => {
    const [start, end] = range || [];
    if (!start || !end) {
      setFortnightsCount(0);
      formik.setFieldValue("startDayPay", null);
      formik.setFieldValue("endDayPay", null);
      formik.setFieldError("startDayPay", undefined);
      formik.setFieldError("endDayPay", undefined);
      return;
    }
    const ns = normalizeStartToFortnight(start);
    const ne = normalizeEndToFortnight(end);
    const count = countFortnights(ns, ne);

    setFortnightsCount(count);
    formik.setFieldValue("startDayPay", ns.unix());
    formik.setFieldValue("endDayPay", ne.unix());
    formik.setFieldError("startDayPay", undefined);
    formik.setFieldError("endDayPay", undefined);

    const currentAmount = Number(formik.values.adminAmount);
    if (currentAmount > 0 && count > 0) {
      const perQuincena = currentAmount / count;
      formik.setFieldValue("amountDiscount", perQuincena.toFixed(2));
    }
  };

  const disabledDateForPeriod = (_current: any) => {
    return false;
  };

  const handleCalendarChange = (dates: any) => {
    if (dates && dates[0]) {
      setTempStartDate(dates[0]);
    } else {
      setTempStartDate(null);
    }
  };

  const handleDecisionChange = (decision: boolean | string) => {
    if (decision === "proposal") {
      formik.setFieldValue("approved", false);
      formik.setFieldValue("isRejected", false);
      formik.setFieldValue("isProposal", true);
      formik.setFieldValue("reviewerComment", "");
    } else if (decision === false) {
      formik.setFieldValue("approved", false);
      formik.setFieldValue("isRejected", true);
      formik.setFieldValue("isProposal", false);
      formik.setFieldValue("adminAmount", undefined);
      formik.setFieldValue("startDayPay", undefined);
      formik.setFieldValue("endDayPay", undefined);
      formik.setFieldValue("reviewerComment", "");
    } else {
      formik.setFieldValue("approved", true);
      formik.setFieldValue("isRejected", false);
      formik.setFieldValue("isProposal", false);
      formik.setFieldValue("reviewerComment", undefined);
    }
  };

  const handlePartnerChange = async (e) => {
    formik.handleChange(e);
    const value = e.target.value;
    if (value) {
      await loadPartnerInfo(Number(value));
      await loadPartnerHistory(Number(value));
    }
  };

  if (initialLoading) {
    return (
      <Layout>
        <div>
          <Loader isLoading={true} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div>
        <Title
          baseTitle="Préstamos"
          basePath="/prestamos"
          title="Editar préstamo"
        />

        <div className="card border-0 m-4 rounded">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Estado del Préstamo</h5>
              <span className={`badge ${getStatusColor()} fs-6 px-3 py-2`}>
                {getLoanStatus()}
              </span>
            </div>
            {isAlreadyProcessed && (
              <div className="alert alert-info mt-3 mb-0">
                <i className="fas fa-info-circle me-2"></i>
                Este préstamo ya ha sido procesado y no puede ser modificado.
              </div>
            )}
          </div>
        </div>

        <div className="d-flex flex-column flex-lg-row gap-4 m-4">
          <div className="flex-lg-grow-0" style={{ width: "30%" }}>
            <div className="card border-0 rounded h-100 shadow-sm">
              <div className="card-body">
                {/* Tabs */}
                <div className="d-flex mb-3">
                  <button
                    className={`btn btn-sm flex-fill me-2 ${
                      activeTab === "info" ? "btn-azul" : "btn-outline-azul"
                    }`}
                    onClick={() => setActiveTab("info")}
                  >
                    <i className="fas fa-user me-1"></i>
                    Información
                  </button>
                  <button
                    className={`btn btn-sm flex-fill ${
                      activeTab === "history" ? "btn-azul" : "btn-outline-azul"
                    }`}
                    onClick={() => setActiveTab("history")}
                  >
                    <i className="fas fa-history me-1"></i>
                    Historial
                  </button>
                </div>

                {/* Tab Content */}
                {activeTab === "info" && (
                  <div className="text-center">
                    <h5 className="mb-3 text-azul">
                      Información del colaborador
                    </h5>
                    {loading ? (
                      <div className="d-flex flex-column align-items-center py-3">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Cargando...</span>
                        </div>
                        <div className="text-muted small mt-2">
                          Cargando colaborador...
                        </div>
                      </div>
                    ) : selectedPartner ? (
                      <>
                        <div className="mb-3">
                          <img
                            src={profileUrl || "/assets/img/avatar.jpg"}
                            alt="Foto de perfil"
                            className="rounded-circle shadow"
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              border: "4px solid #f8f9fa",
                            }}
                            onError={(e: any) => {
                              e.target.src = "/assets/img/avatar.jpg";
                            }}
                          />
                        </div>
                        <div className="text-center">
                          <h6 className="mb-1 fw-bold fs-4 text-dark">
                            {toCamelCase(
                              `${selectedPartner?.person?.firstName || ""} ${
                                selectedPartner?.person?.lastName || ""
                              } ${
                                selectedPartner?.person?.secondLastName || ""
                              }`
                            )}
                          </h6>
                          <div className="text-muted small mb-1">Puesto</div>
                          <div className="fw-semibold fs-6 text-dark">
                            {selectedPartner?.candidate?.vacancy?.position
                              ?.name || "-"}
                          </div>
                          <div className="text-muted small mt-3 mb-1">
                            Sucursal
                          </div>
                          <div className="fw-semibold fs-6 text-dark">
                            {selectedPartner?.candidate?.branch?.name || "-"}
                          </div>
                        </div>
                        <div className="alert alert-info mt-3 text-center">
                          <i className="fa fa-info-circle"></i> Se actualizará
                          el préstamo para este colaborador
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mb-3">
                          <img
                            src={"/assets/img/avatar.jpg"}
                            alt="Foto de perfil"
                            className="rounded-circle shadow"
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                              border: "4px solid #f8f9fa",
                            }}
                          />
                        </div>
                        <div className="text-center text-muted small mb-3">
                          Selecciona un colaborador para ver su información
                        </div>
                        <div className="alert alert-info mb-2 text-start">
                          Rellena los campos para editar el préstamo
                        </div>
                        <div className="alert alert-warning mb-0 text-start">
                          El monto por quincena se calcula automáticamente según
                          el periodo
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Historial Tab */}
                {activeTab === "history" && (
                  <div>
                    <h5 className="mb-3 text-azul">Historial de préstamos</h5>
                    {loadingHistory ? (
                      <div className="d-flex flex-column align-items-center py-3">
                        <div
                          className="spinner-border text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Cargando...</span>
                        </div>
                        <div className="text-muted small mt-2">
                          Cargando historial...
                        </div>
                      </div>
                    ) : partnerHistory.length > 0 ? (
                      <div
                        className="overflow-auto"
                        style={{ maxHeight: "400px" }}
                      >
                        {partnerHistory.map((item: any, index: number) => (
                          <div
                            key={item.id || index}
                            className="card mb-2 border-light shadow-sm"
                          >
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="fw-semibold text-dark">
                                  {getCurrencyFormat(item.requestAmount)}
                                </div>
                                <span
                                  className={`badge ${
                                    item.status === "pending"
                                      ? "bg-warning"
                                      : item.status === "approved"
                                      ? "bg-success"
                                      : "bg-danger"
                                  }`}
                                >
                                  {item.status === "pending"
                                    ? "Pendiente"
                                    : item.status === "approved"
                                    ? "Aprobado"
                                    : "Rechazado"}
                                </span>
                              </div>
                              <div className="text-muted small mb-1">
                                <i className="fas fa-dollar-sign me-1"></i>
                                Descuento:{" "}
                                {getCurrencyFormat(item.amountDiscount)}
                              </div>
                              {item.startDayPay && (
                                <div className="text-muted small mb-1">
                                  <i className="fas fa-calendar me-1"></i>
                                  Inicio:{" "}
                                  {dayjs
                                    .unix(item.startDayPay)
                                    .format("DD/MM/YYYY")}
                                </div>
                              )}
                              {item.endDayPay && (
                                <div className="text-muted small mb-1">
                                  <i className="fas fa-calendar me-1"></i>
                                  Fin:{" "}
                                  {dayjs
                                    .unix(item.endDayPay)
                                    .format("DD/MM/YYYY")}
                                </div>
                              )}
                              {item.note && (
                                <div className="text-muted small">
                                  <i className="fas fa-sticky-note me-1"></i>
                                  {item.note}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <i className="fas fa-inbox fa-2x mb-2"></i>
                        <div>No hay historial de préstamos</div>
                        <small>
                          Este colaborador no tiene registros previos
                        </small>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex-lg-grow-0" style={{ width: "70%" }}>
            <div className="card border-0 rounded mb-4">
              <Panel>
                <PanelHeader noButton={true} className="bg-azul">
                  Editar datos del préstamo
                </PanelHeader>
                <div className="p-4">
                  <ConfigProvider locale={esES}>
                    <div className="row">
                      {/* <div className="col-md-12 mb-3">
                        <label className="form-label">Colaborador</label>
                        <select
                          className="form-select"
                          name="partnerId"
                          value={formik.values.partnerId}
                          onChange={handlePartnerChange}
                          onBlur={formik.handleBlur}
                          disabled={true}
                        >
                          <option value="">Selecciona un colaborador</option>
                          {partners?.map((p: any) => (
                            <option key={p.id} value={p.id}>
                              {p.person
                                ? `${p.person.firstName} ${p.person.lastName}`
                                : p.id}
                            </option>
                          ))}
                        </select>
                        {errorFor("partnerId")}
                      </div> */}
                      <div className="col-md-12 mb-4">
                        {/* solicitado por el colaborador */}
                        <div className="mb-3">
                          <div className="fw-bold fs-6 text-azul">
                            Solicitado por el colaborador
                          </div>
                        </div>
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 p-3 mb-3  rounded-3 bg-gray-100 border border-gray-200">
                          <div className="flex-fill text-center">
                            <div className="mb-1 text-muted small">
                              Monto solicitado
                            </div>
                            <div className="fw-bold fs-5 text-black">
                              {getCurrencyFormat(
                                formik.values.requestAmount || 0
                              )}
                            </div>
                          </div>
                          <div
                            style={{
                              height: "50px",
                              borderLeft: "1px solid #e2e8f0",
                            }}
                            className="d-none d-md-block mx-2"
                          ></div>
                          <div className="flex-fill text-center">
                            <div className="mb-1 text-muted small">
                              Quincenas solicitadas
                            </div>
                            <div className="fw-bold fs-5 text-black">
                              {formik.values.fortnights || "-"}
                            </div>
                          </div>
                          {/* monto por quincena */}
                          <div className="flex-fill text-center">
                            <div className="mb-1 text-muted small">
                              Monto por quincena
                            </div>
                            <div className="fw-bold fs-5 text-black">
                              {displayCurrency(
                                formik.values.amountPerFortnight
                              ) || "-"}
                            </div>
                          </div>

                          <div
                            style={{
                              height: "50px",
                              borderLeft: "1px solid #e2e8f0",
                            }}
                            className="d-none d-md-block mx-2"
                          ></div>
                          <div className="flex-fill text-center">
                            <div className="mb-1 text-muted small">
                              Fecha de recepción
                            </div>
                            <div className="fw-bold fs-5 text-black">
                              {formik.values.requestedReceiveDate
                                ? dayjs
                                    .unix(
                                      Number(formik.values.requestedReceiveDate)
                                    )
                                    .format("DD MMM YYYY")
                                : "-"}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Por revisar */}
                      <div className="col-md-12 mb-3">
                        <div className="fw-bold fs-6 text-azul">
                          Por revisar
                        </div>
                      </div>
                      {/* Campos para APROBAR o PROPUESTA */}
                      {(formik.values.approved === true ||
                        formik.values.isProposal === true) && (
                        <>
                          <div className="col-md-4 mb-3">
                            <label className="form-label">
                              Monto aprobado por administrador
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="adminAmount"
                              value={
                                isEditingAdminAmount
                                  ? adminAmountInput
                                  : displayCurrency(formik.values.adminAmount)
                              }
                              disabled={isAlreadyProcessed}
                              onFocus={() => {
                                if (isAlreadyProcessed) return;
                                setIsEditingAdminAmount(true);
                                const current = formik.values.adminAmount;
                                setAdminAmountInput(
                                  current === null ||
                                    current === undefined ||
                                    current === ""
                                    ? ""
                                    : String(current)
                                );
                              }}
                              onChange={(e) => {
                                const val = e.target.value;
                                setAdminAmountInput(val);
                                const parsed = parseCurrency(val || "");
                                if (isNaN(parsed)) {
                                  formik.setFieldValue("adminAmount", "");
                                } else {
                                  formik.setFieldValue("adminAmount", parsed);
                                }
                              }}
                              onBlur={(e) => {
                                formik.handleBlur(e);
                                setIsEditingAdminAmount(false);
                                const parsed = parseCurrency(
                                  adminAmountInput || ""
                                );
                                if (isNaN(parsed)) {
                                  setAdminAmountInput("");
                                  formik.setFieldValue("adminAmount", "");
                                } else {
                                  setAdminAmountInput(
                                    getCurrencyFormat(parsed)
                                  );
                                  formik.setFieldValue("adminAmount", parsed);
                                }
                              }}
                            />
                            {errorFor("adminAmount")}
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label">
                              Monto por quincena
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              name="amountDiscount"
                              value={displayCurrency(
                                formik.values.amountDiscount
                              )}
                              disabled
                            />
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label">Quincenas</label>
                            <input
                              type="number"
                              className="form-control"
                              value={fortnightsCount || 0}
                              disabled
                            />
                          </div>

                          <div className="col-md-12 mb-3">
                            <label className="form-label">
                              Periodo de descuento
                            </label>
                            <DatePicker.RangePicker
                              style={{
                                width: "100%",
                                borderColor:
                                  (formik.touched.startDayPay &&
                                    formik.errors.startDayPay) ||
                                  (formik.touched.endDayPay &&
                                    formik.errors.endDayPay)
                                    ? "#dc3545"
                                    : undefined,
                              }}
                              format="DD/MM/YYYY"
                              disabled={isAlreadyProcessed}
                              disabledDate={disabledDateForPeriod}
                              onCalendarChange={handleCalendarChange}
                              value={
                                formik.values.startDayPay &&
                                formik.values.endDayPay
                                  ? [
                                      dayjs.unix(formik.values.startDayPay),
                                      dayjs.unix(formik.values.endDayPay),
                                    ]
                                  : null
                              }
                              renderExtraFooter={() => (
                                <div className="text-muted small bg-yellow px-2 rounded-3">
                                  <i className="fa fa-info-circle"></i>{" "}
                                  Selecciona las fechas del periodo de descuento
                                </div>
                              )}
                              onChange={(dates) => {
                                setTempStartDate(null);
                                handlePeriodDateChange(dates);
                              }}
                            />
                          </div>
                        </>
                      )}

                      <div className="col-md-12 mb-3">
                        <label className="form-label">Decisión *</label>
                        <div className="mt-2">
                          <div className="form-check form-check-inline">
                            <input
                              type="radio"
                              name="approved"
                              value="true"
                              className="form-check-input"
                              id="approve"
                              checked={formik.values.approved === true}
                              onChange={() => handleDecisionChange(true)}
                              disabled={isAlreadyProcessed}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="approve"
                            >
                              Aprobar
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              type="radio"
                              name="approved"
                              value="false"
                              className="form-check-input"
                              id="reject"
                              checked={formik.values.approved === false}
                              onChange={() => handleDecisionChange(false)}
                              disabled={isAlreadyProcessed}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="reject"
                            >
                              Rechazar
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              type="radio"
                              name="approved"
                              value="proposal"
                              className="form-check-input"
                              id="proposal"
                              checked={formik.values.isProposal === true}
                              onChange={() => handleDecisionChange("proposal")}
                              disabled={isAlreadyProcessed}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="proposal"
                            >
                              Propuesta
                            </label>
                          </div>
                        </div>
                        {formik.touched.approved && formik.errors.approved && (
                          <div className="text-danger small mt-1">
                            {formik.errors.approved}
                          </div>
                        )}
                      </div>

                      {/* Campo de comentario para RECHAZAR */}
                      {formik.values.isRejected === true && (
                        <div className="col-md-12 mb-3">
                          <label className="form-label">
                            Comentario del revisor{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <textarea
                            className={`form-control ${
                              formik.touched.reviewerComment &&
                              formik.errors.reviewerComment
                                ? "is-invalid"
                                : ""
                            }`}
                            rows={3}
                            placeholder="Comentario del revisor"
                            value={formik.values.reviewerComment}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "reviewerComment",
                                e.target.value
                              )
                            }
                            onBlur={formik.handleBlur}
                            name="reviewerComment"
                            disabled={isAlreadyProcessed}
                          />
                          {formik.touched.reviewerComment &&
                            formik.errors.reviewerComment && (
                              <div className="invalid-feedback">
                                {formik.errors.reviewerComment}
                              </div>
                            )}
                        </div>
                      )}

                      {/* Campo de comentario para PROPUESTA */}
                      {formik.values.isProposal === true && (
                        <div className="col-md-12 mb-3">
                          <label className="form-label">
                            Comentario de la propuesta{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <textarea
                            className={`form-control ${
                              formik.touched.proposalComment &&
                              formik.errors.proposalComment
                                ? "is-invalid"
                                : ""
                            }`}
                            rows={3}
                            placeholder="Explica la propuesta al empleado"
                            value={formik.values.proposalComment}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "proposalComment",
                                e.target.value
                              )
                            }
                            onBlur={formik.handleBlur}
                            name="proposalComment"
                            disabled={isAlreadyProcessed}
                          />
                          {formik.touched.proposalComment &&
                            formik.errors.proposalComment && (
                              <div className="invalid-feedback">
                                {formik.errors.proposalComment}
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  </ConfigProvider>
                </div>
              </Panel>
            </div>
          </div>
        </div>

        <div className="card border-0 m-4 rounded p-3">
          <div className="row justify-content-end">
            <div className="col-md-3">
              <Button
                title="Cancelar"
                variant="secondary"
                onClick={() => navigate("/prestamos")}
                type="button"
                disabled={loading}
              />
            </div>
            {!isAlreadyProcessed ? (
              <div className="col-md-3">
                <Button
                  title="Actualizar"
                  variant="azul"
                  onClick={() => formik.handleSubmit()}
                  type="button"
                  disabled={loading}
                />
              </div>
            ) : (
              <div className="col-md-3">
                <Button
                  title={`Préstamo ${getLoanStatus().toLowerCase()}`}
                  variant="secondary"
                  type="button"
                  disabled={true}
                />
              </div>
            )}
          </div>
        </div>

        <Loader isLoading={loading} />
      </div>
    </Layout>
  );
};

export default EditLoan;
